








































import { computed, defineComponent, PropType, ref, watch } from "@vue/composition-api";
import { TranslateResult } from "vue-i18n";
import { useCreditCardAssignment } from "@/hooks/useCreditCardAssignment";
import useSystemSettings from "@/hooks/useSystemSettings";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, FullPriceData, getMainCurrencyCode } from "@/lib/moneyHelpers";
import { getAvailableSystems, isBalanceSystem, myBalanceFakeSystem } from "@/lib/paymentSystemsHelpers";
import { IntegrationType } from "@/payments/integrations/const";
import { PurchaseType } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseSpinner from "@/components/BaseSpinner";
import PaymentMethodsList from "./components/PaymentMethodsList";
import { PaymentSystemListItem } from "./components/PaymentMethodsList/PaymentMethodsList.vue";
import { PaymentConfirmationData, purchaseTypeNames } from "./const";

export default defineComponent({
  name: "PaymentConfirmation",
  components: { BaseSpinner, PaymentMethodsList, BaseConfirm, BaseButton },
  props: {
    show: { type: Boolean, required: true },
    processing: { type: Boolean, required: true },
    purchaseType: { type: String as PropType<PurchaseType>, required: true },
    priceData: { type: Object as PropType<FullPriceData>, required: true },
    oldPriceData: { type: Object as PropType<FullPriceData> },
    title: { type: String as PropType<TranslateResult>, default: $t("Payment confirmation") },
    buttonText: { type: String as PropType<TranslateResult>, default: $t("Pay now") },
  },
  setup(props, { emit }) {
    const { publicPaymentSystems } = useSystemSettings();
    const { creditCards, getCreditCardAssignment } = useCreditCardAssignment();
    const forceHide = ref(false);
    const fetching = ref(false);
    const selectedSystemId = ref("");

    const availableSystems = computed<PaymentSystemListItem[]>(() => {
      return getAvailableSystems([myBalanceFakeSystem, ...publicPaymentSystems.value], props.purchaseType).map(
        ({ system, integration }) => {
          const card = creditCards.value.find(i => i.paymentSystemId === system.id);
          return { system, name: integration.name, logo: integration.logo, card, type: integration.type };
        }
      );
    });

    const selectedSystem = computed(() => availableSystems.value.find(s => s.system.id === selectedSystemId.value));

    const selectedSystemIsBalance = computed(() => isBalanceSystem(selectedSystem.value?.system.type ?? ""));

    const oldCost = computed(() => {
      if (!props.oldPriceData) return;

      return selectedSystemIsBalance.value ? props.oldPriceData.creditPrice : props.oldPriceData.price;
    });

    const paymentMethod = computed(() => {
      if (!selectedSystem.value) return $t("Not selected");
      if (isBalanceSystem(selectedSystem.value.system.type)) return $t("My balance");
      if (selectedSystem.value.type === IntegrationType.CRYPTO) return $t("Crypto");
      else return $t("Credit card");
    });

    const details = computed(() => [
      { title: $t("Purchase type"), value: purchaseTypeNames[props.purchaseType] },
      { title: $t("Payment method"), value: paymentMethod.value },
    ]);

    const onSubmit = () => {
      if (!selectedSystem.value) return;
      const fromBalance = isBalanceSystem(selectedSystem.value.system.type);
      const data: PaymentConfirmationData = {
        fromBalance,
        paymentSystemId: fromBalance ? undefined : selectedSystemId.value,
      };
      emit("purchase", data);
    };

    const setPredefinedSystem = () => {
      if (availableSystems.value.length === 1) {
        selectedSystemId.value = availableSystems.value[0].system.id;
        if (props.purchaseType === PurchaseType.TOP_UP) {
          forceHide.value = true;
          onSubmit();
        }
      } else if (availableSystems.value.length > 1) {
        const balanceSystem = availableSystems.value.find(s => isBalanceSystem(s.system.type));
        if (balanceSystem) {
          selectedSystemId.value = balanceSystem.system.id;
        } else {
          const systemsWhitCard = availableSystems.value.filter(s => s.card);
          if (systemsWhitCard.length === 1) selectedSystemId.value = systemsWhitCard[0].system.id;
        }
      }
    };

    const getData = async () => {
      try {
        fetching.value = true;
        await getCreditCardAssignment();
        setPredefinedSystem();
      } finally {
        fetching.value = false;
      }
    };

    getData();

    watch(selectedSystemId, () => emit("input", selectedSystemId.value), { immediate: true });

    return {
      details,
      availableSystems,
      fetching,
      selectedSystemId,
      selectedSystemIsBalance,
      forceHide,
      oldCost,
      onSubmit,
      getMainCurrencyCode,
      formatMoneyString,
    };
  },
});
